import { useMemo, useState } from "react";
import { Button, Checkbox, Message } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { Modal } from "../../components/Modal/Modal";
import { SafeMarkdown } from "../../components/SafeMarkdown/SafeMarkdown";
import { AFRICAN_COUNTRIES, OTHER_COUNTRIES } from "../../components/ConsentGate/countries";
import { useTranslation } from "../../i18n/LocaleProvider";
import { cn } from "../../utils/bem";
import "./DiscoverModals.prefix.css";

const block = cn("join-modal");

// Same country grouping + age options ConsentGate uses, so the join-time
// signing collects the identical legal fields as the annotate-time gate.
const COUNTRY_GROUPS: { group: string; entries: [string, string][] }[] = [
  { group: "African Union members", entries: AFRICAN_COUNTRIES },
  { group: "Other countries", entries: OTHER_COUNTRIES.slice().sort((a, b) => a[1].localeCompare(b[1])) },
];
type Age = "" | "18_or_over" | "under_18_with_guardian" | "guardian_signing";
const AGE_OPTIONS: [Age, string][] = [
  ["18_or_over", "I am 18 or older."],
  ["under_18_with_guardian", "I am under 18 and my legal guardian has consented to my participation."],
  ["guardian_signing", "I am signing on behalf of a minor as their legal guardian."],
];

type Purpose = { key: string; label?: string; description?: string; required?: boolean };
type ConsentRequirement = {
  requirement_id: number;
  template_id: number;
  title: string;
  kind?: string;
  body: string;
  version: number;
  purposes: Purpose[];
  optional: boolean;
};

export type DiscoverProjectForJoin = {
  id: number;
  title: string | null;
  description: string | null;
  organization: { id: number; title: string } | null;
  consent_requirements?: ConsentRequirement[];
};

/**
 * JoinProjectModal — the consent gate before joining a public+open project.
 *
 * Signs through the SAME consent module + endpoint as the annotate-time
 * ``ConsentGate``, collecting the identical legal fields (country of
 * residence, age attestation, guardian/minor, authority, typed name) plus
 * per-template agreement + granular purposes. Every agreement shown is a
 * real ``ConsentTemplate`` on the project (no hardcoded disclosure). On
 * confirm we record one ``ConsentSignature`` per agreed template, then
 * join — satisfying the annotate-time gate so the contributor isn't
 * re-prompted.
 */
export const JoinProjectModal = ({
  project,
  onClose,
  onJoined,
  inviteToken,
}: {
  project: DiscoverProjectForJoin;
  onClose: () => void;
  onJoined: (projectId: number, status?: "active" | "pending") => void;
  /** Facilitator invite token (Phase F) — attributes this join to the
   *  facilitator whose link brought the contributor here. */
  inviteToken?: string;
}) => {
  const { t } = useTranslation();
  const api = useAPI();
  const { user } = useAuth() as { user: { first_name?: string; last_name?: string; email?: string } | null };
  const profileName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();

  const requirements = project.consent_requirements ?? [];
  const requiredConsents = useMemo(() => requirements.filter((r) => !r.optional), [requirements]);
  const optionalConsents = useMemo(() => requirements.filter((r) => r.optional), [requirements]);
  // Nothing about consent is hardcoded: the signing flow exists ONLY when
  // the owner attached at least one ConsentTemplate to the project. A
  // project with no consents joins in one click.
  const hasConsents = requirements.length > 0;

  // Signing details — collected once, applied to every signature (same as
  // ConsentGate: these are properties of the human, not of each document).
  const [country, setCountry] = useState("");
  const [age, setAge] = useState<Age>("");
  const [minorName, setMinorName] = useState("");
  const [signedName, setSignedName] = useState(profileName);
  const [authority, setAuthority] = useState(false);

  const [agreedConsents, setAgreedConsents] = useState<Set<number>>(() => new Set());
  const [purposesByTemplate, setPurposesByTemplate] = useState<Record<number, Set<string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Set when a review-mode join returns ``pending`` — the modal switches to
  // a "request submitted" confirmation instead of navigating into a project
  // the contributor can't reach yet.
  const [submitted, setSubmitted] = useState(false);

  const isMinorFlow = age === "under_18_with_guardian" || age === "guardian_signing";

  const toggleConsent = (id: number) =>
    setAgreedConsents((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const togglePurpose = (templateId: number, key: string) =>
    setPurposesByTemplate((prev) => {
      const set = new Set(prev[templateId] ?? []);
      set.has(key) ? set.delete(key) : set.add(key);
      return { ...prev, [templateId]: set };
    });
  const requiredPurposesMet = (r: ConsentRequirement) =>
    (r.purposes || [])
      .filter((p) => p.required)
      .every((p) => (purposesByTemplate[r.template_id] ?? new Set()).has(p.key));

  // Any template the contributor is actually agreeing to (all required ones,
  // plus any optional ones they ticked). Signing details are only demanded
  // when there's at least one signature to attach them to.
  const willSign = requirements.filter((r) => agreedConsents.has(r.template_id));
  const showSigning = requiredConsents.length > 0 || willSign.length > 0;

  // Gate: the same legal fields ConsentGate requires, demanded only when a
  // signature will be recorded, plus every required consent agreed with its
  // required purposes.
  const detailsComplete =
    !!country && !!age && (!isMinorFlow || !!minorName.trim()) && authority && signedName.trim().length >= 2;
  const canJoin =
    requiredConsents.every((r) => agreedConsents.has(r.template_id) && requiredPurposesMet(r)) &&
    (willSign.length === 0 || detailsComplete);

  const signConsent = async (r: ConsentRequirement) => {
    const res = await api.callApi("addConsentSignature", {
      body: {
        template: r.template_id,
        signed_name: signedName.trim(),
        project: project.id,
        country_of_residence: country,
        age_attestation: age,
        minor_name: isMinorFlow ? minorName.trim() : "",
        purposes_accepted: Array.from(purposesByTemplate[r.template_id] ?? new Set()),
      },
      suppressError: true,
    });
    if (!res || res.error) {
      // Offline: the global offline modal explains; a sentinel error
      // with no message keeps the inline banner empty (onConfirm's
      // catch only renders non-empty messages).
      if ((res as any)?.response?.code === "offline") {
        throw new Error("");
      }
      throw new Error(res?.response?.message || res?.error || `Couldn't record agreement to "${r.title}".`);
    }
  };

  const onConfirm = async () => {
    if (!canJoin || busy) return;
    setError("");
    setBusy(true);
    try {
      const toSign = requirements.filter((r) => agreedConsents.has(r.template_id));
      for (const r of toSign) {
        await signConsent(r); // eslint-disable-line no-await-in-loop
      }
      const res = await api.callApi("projectJoin", {
        params: { pk: project.id },
        body: inviteToken ? { invite_token: inviteToken } : {},
        suppressError: true,
      });
      if (!res || res.error) {
        // Offline: the global offline modal (fired by callApi's
        // never-suppressed offline hook) already explains — no
        // inline banner on top of it. Leave the dialog as-is so the
        // user can just try again once reconnected.
        if ((res as any)?.response?.code === "offline") {
          return;
        }
        throw new Error(res?.error || "Couldn't join this project. Try again in a moment.");
      }
      const pending = res.code === "pending" || res.status === "pending";
      if (pending) {
        setSubmitted(true);
        return;
      }
      onJoined(project.id, "active");
    } catch (e) {
      // An empty message is the offline sentinel from signConsent —
      // the global offline modal already explains, so no banner.
      const msg = (e as Error)?.message;
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible
      onHide={onClose}
      title={
        submitted
          ? t("pages.discover.request_submitted_title", undefined, "Request submitted")
          : `Join ${project.title ?? "this project"}`
      }
      style={{ width: 640 }}
      footer={
        submitted ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="primary" size="small" onClick={() => onJoined(project.id, "pending")}>
              Done
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="negative" look="outlined" size="small" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" size="small" onClick={onConfirm} disabled={!canJoin || busy}>
              {busy ? "Joining…" : "Agree and join"}
            </Button>
          </div>
        )
      }
    >
      {submitted ? (
        <div className={block.toClassName()}>
          <p className={block.elem("lead").toClassName()}>
            {t(
              "pages.discover.request_submitted_body",
              { project: project.title ?? "this project" },
              `Your request to join ${project.title ?? "this project"} has been sent. The project owner reviews each request, and you'll get access to start annotating once they approve it.`,
            )}
          </p>
          <p className={block.elem("muted").toClassName()}>
            {t(
              "pages.discover.request_submitted_note",
              undefined,
              "Any agreements you signed are recorded and stay valid, so you won't be asked again after approval.",
            )}
          </p>
        </div>
      ) : (
        <div className={block.toClassName()}>
          {error ? (
            <Message variant="negative" size="small">
              {error}
            </Message>
          ) : null}

          {project.organization?.title ? (
            <div className={block.elem("muted").toClassName()}>by {project.organization.title}</div>
          ) : null}
          {project.description ? <p className={block.elem("lead").toClassName()}>{project.description}</p> : null}

          {!hasConsents ? (
            <p className={block.elem("muted").toClassName()}>
              This project has no agreements to sign. Click join to start contributing — you can opt out at any time.
            </p>
          ) : null}

          {/* Signing details — same fields ConsentGate collects. Shown only
            when at least one consent will actually be signed. */}
          {showSigning && (
            <section className={block.elem("section").toClassName()}>
              <h3 className={block.elem("section-title").toClassName()}>Your details</h3>

              <label className={block.elem("field").toClassName()}>
                <span>Country of residence</span>
                <select
                  className={block.elem("input").toClassName()}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select…</option>
                  {COUNTRY_GROUPS.map((g) => (
                    <optgroup key={g.group} label={g.group}>
                      {g.entries.map(([code, name]) => (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>

              <fieldset className={block.elem("fieldset").toClassName()}>
                <legend className={block.elem("legend").toClassName()}>Age</legend>
                {AGE_OPTIONS.map(([k, label]) => (
                  <label key={k} className={block.elem("check").toClassName()}>
                    <input
                      type="radio"
                      name="join-age"
                      value={k}
                      checked={age === k}
                      onChange={() => setAge(k)}
                      disabled={busy}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </fieldset>

              {isMinorFlow && (
                <label className={block.elem("field").toClassName()}>
                  <span>{age === "guardian_signing" ? "Minor's full name" : "Minor's full name (the annotator)"}</span>
                  <input
                    className={block.elem("input").toClassName()}
                    type="text"
                    value={minorName}
                    onChange={(e) => setMinorName(e.target.value)}
                    disabled={busy}
                  />
                </label>
              )}

              <label className={block.elem("field").toClassName()}>
                <span>{age === "guardian_signing" ? "Guardian, your full legal name" : "Your full legal name"}</span>
                <input
                  className={block.elem("input").toClassName()}
                  type="text"
                  value={signedName}
                  onChange={(e) => {
                    if (!profileName) setSignedName(e.target.value);
                  }}
                  readOnly={!!profileName}
                  disabled={busy}
                  placeholder="e.g. Idris Abdulmumin"
                />
                <span className={block.elem("muted").toClassName()}>
                  {profileName
                    ? "This comes from your profile and is used to attribute your signatures."
                    : "Type your legal name as it appears on official identification."}
                </span>
              </label>

              <label className={block.elem("check").toClassName()}>
                <Checkbox
                  checked={authority}
                  onChange={() => setAuthority((v) => !v)}
                  disabled={busy}
                  ariaLabel="Confirm authority to sign"
                />
                <span>
                  I confirm I have the authority to sign these agreements (for myself, or as the legal guardian of the
                  named minor) and that the name above is my legal name.
                </span>
              </label>
            </section>
          )}

          {requiredConsents.length > 0 && (
            <section className={block.elem("section").toClassName()}>
              <h3 className={block.elem("section-title").toClassName()}>Required agreements</h3>
              <p className={block.elem("muted").toClassName()}>
                Read and agree to each to join. You won't be asked again when you start annotating.
              </p>
              {requiredConsents.map((r) => (
                <ConsentItem
                  key={r.template_id}
                  requirement={r}
                  agreed={agreedConsents.has(r.template_id)}
                  onToggleAgreed={() => toggleConsent(r.template_id)}
                  acceptedPurposes={purposesByTemplate[r.template_id] ?? new Set()}
                  onTogglePurpose={(key) => togglePurpose(r.template_id, key)}
                  disabled={busy}
                />
              ))}
            </section>
          )}

          {optionalConsents.length > 0 && (
            <section className={block.elem("section").toClassName()}>
              <h3 className={block.elem("section-title").toClassName()}>Optional agreements</h3>
              {optionalConsents.map((r) => (
                <ConsentItem
                  key={r.template_id}
                  requirement={r}
                  optional
                  agreed={agreedConsents.has(r.template_id)}
                  onToggleAgreed={() => toggleConsent(r.template_id)}
                  acceptedPurposes={purposesByTemplate[r.template_id] ?? new Set()}
                  onTogglePurpose={(key) => togglePurpose(r.template_id, key)}
                  disabled={busy}
                />
              ))}
            </section>
          )}
        </div>
      )}
    </Modal>
  );
};

const ConsentItem = ({
  requirement: r,
  agreed,
  onToggleAgreed,
  acceptedPurposes,
  onTogglePurpose,
  disabled,
  optional = false,
}: {
  requirement: ConsentRequirement;
  agreed: boolean;
  onToggleAgreed: () => void;
  acceptedPurposes: Set<string>;
  onTogglePurpose: (key: string) => void;
  disabled: boolean;
  optional?: boolean;
}) => {
  const item = cn("consent-item");
  const [readOpen, setReadOpen] = useState(false);
  return (
    <div className={item.mod({ agreed }).toClassName()}>
      <div className={item.elem("top").toClassName()}>
        <span className={item.elem("title").toClassName()}>
          {r.title}
          {optional ? "" : ""}
        </span>
        <button type="button" className={item.elem("read").toClassName()} onClick={() => setReadOpen(true)}>
          Read
        </button>
      </div>

      {readOpen && (
        <Modal
          visible
          onHide={() => setReadOpen(false)}
          title={r.title}
          style={{ width: 620 }}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="primary" size="small" onClick={() => setReadOpen(false)}>
                OK
              </Button>
            </div>
          }
        >
          <div style={{ fontSize: "var(--font-size-body-small)", lineHeight: 1.55 }}>
            <SafeMarkdown text={r.body} />
          </div>
        </Modal>
      )}

      {(r.purposes || []).length > 0 && (
        <div className={item.elem("purposes").toClassName()}>
          {r.purposes.map((p) => (
            <label
              key={p.key}
              className={cn("join-modal").elem("check").toClassName()}
              style={{ fontSize: "var(--font-size-caption)" }}
            >
              <Checkbox
                checked={acceptedPurposes.has(p.key)}
                onChange={() => onTogglePurpose(p.key)}
                disabled={disabled}
                ariaLabel={p.label || p.key}
              />
              <span>
                {p.label || p.key}
                {p.required ? (
                  <span aria-hidden style={{ color: "var(--color-negative-content)", marginLeft: 4 }}>
                    *
                  </span>
                ) : null}
                {p.description ? (
                  <div className={cn("join-modal").elem("muted").toClassName()}>{p.description}</div>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      )}

      <label className={cn("join-modal").elem("check").toClassName()}>
        <Checkbox checked={agreed} onChange={onToggleAgreed} disabled={disabled} ariaLabel={`I agree to ${r.title}`} />
        <span>
          I have read and agree to <strong>{r.title}</strong>.
        </span>
      </label>
    </div>
  );
};

export default JoinProjectModal;
